import { RecurlyError } from 'recurly.js';
import {
  ProcessCustomBillingInfoProps,
  RecurlyToken,
  SplitNameResult,
  TokenizationOptions,
} from './types';

export const splitFullName = (fullName: string): SplitNameResult => {
  const trimmedName = fullName.trim();
  const firstSpaceIndex = trimmedName.indexOf(' ');

  if (firstSpaceIndex === -1) {
    return { firstName: trimmedName, lastName: '' };
  }

  return {
    firstName: trimmedName.slice(0, firstSpaceIndex),
    lastName: trimmedName.slice(firstSpaceIndex + 1),
  };
};

const createRecurlyInput = (name: string, value: string): HTMLInputElement => {
  const input = document.createElement('input');
  input.setAttribute('data-recurly', name);
  input.setAttribute('type', 'hidden');
  input.value = value;
  return input;
};

const appendFieldsToForm = (
  formElement: HTMLFormElement,
  fields: Array<{ name: string; value: string }>,
): HTMLInputElement[] => {
  const inputs = fields.map(({ name, value }) => {
    const input = createRecurlyInput(name, value);
    formElement.appendChild(input);
    return input;
  });
  return inputs;
};

export const handleRecurlyTokenization = async ({
  formElement,
  temporaryInputs,
  recurly,
  onError,
}: TokenizationOptions): Promise<RecurlyToken> => {
  try {
    const tokenResult = await new Promise<RecurlyToken>((resolve, reject) => {
      recurly.token(formElement, (err, token) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(token);
      });
    });

    return tokenResult;
  } catch (err: unknown) {
    onError?.(err as RecurlyError);
    throw new Error((err as RecurlyError).message || 'Error processing card');
  } finally {
    temporaryInputs.forEach((input) => formElement.removeChild(input));
  }
};

export const processCustomBillingInfo = async ({
  formElement,
  formData,
  recurly,
  onError,
}: ProcessCustomBillingInfoProps) => {
  const { firstName, lastName } = splitFullName(formData.fullName);
  const temporaryInputs = appendFieldsToForm(formElement, [
    { name: 'first_name', value: firstName },
    { name: 'last_name', value: lastName },
    { name: 'postal_code', value: formData.postal_code },
    { name: 'country', value: formData.country },
  ]);
  return handleRecurlyTokenization({ formElement, temporaryInputs, formData, recurly, onError });
};

export const getErrorMessage = (field: string): string => {
  const displayMap: Record<string, string> = {
    postal_code: 'zip code',
    country: 'country',
    name: 'name',
    lastName: 'last name',
  };
  return `Please enter a valid ${displayMap[field]}.`;
};

export const validateFullName = (value: string): string | undefined => {
  if (value.trim().length < 3) {
    return getErrorMessage('name');
  }

  const { lastName } = splitFullName(value);
  if (!lastName) {
    return getErrorMessage('lastName');
  }

  return undefined;
};

export const validateField = (name: string, value: string): string | undefined => {
  if (!value.trim()) {
    return 'This is a required field.';
  }

  if (name === 'fullName') {
    return validateFullName(value);
  }

  return undefined;
};
